<template>
  <q-btn size="sm" flat round dense color="white"
         :icon="appInboxStore.hasUnread ? 'notifications_active' : 'notifications_none'">
    <q-badge v-if="appInboxStore.hasUnread" color="red" floating>{{ appInboxStore.unread }}</q-badge>
    <q-tooltip>{{ tt('appInbox.title', 'Notifications') }}</q-tooltip>

    <q-menu ref="menu" class="app-notif-menu" :anchor="isMobile ? 'bottom middle' : 'bottom right'"
            :self="isMobile ? 'top middle' : 'top right'" :max-height="isMobile ? '85vh' : undefined">
      <q-list :style="menuStyle" class="notif-panel">
        <!-- En-tête moderne : dégradé + formes géométriques (style pages SOS). -->
        <div class="notif-head">
          <span class="nh-shape nh-circle"></span>
          <span class="nh-shape nh-ring"></span>
          <span class="nh-shape nh-diamond"></span>
          <div class="notif-head__row">
            <q-icon name="notifications" size="22px" class="q-mr-sm" />
            <span class="notif-head__title">{{ tt('appInbox.title', 'Notifications') }}</span>
            <q-badge v-if="appInboxStore.unread" color="red" class="q-ml-sm">{{ appInboxStore.unread }}</q-badge>
            <q-space />
            <q-btn v-if="appInboxStore.hasUnread" flat dense round size="sm" color="white" icon="done_all" @click="markAll">
              <q-tooltip>{{ tt('appInbox.markAll', 'Tout marquer lu') }}</q-tooltip>
            </q-btn>
          </div>
        </div>

        <!-- Activation des notifications push OS (fusion de l'ancien toggle) -->
        <q-item v-if="appPushStore.supported && !appPushStore.isBlocked">
          <q-item-section avatar>
            <q-icon :name="appPushStore.isEnabled ? 'notifications_active' : 'notifications_off'"
                    :color="appPushStore.isEnabled ? 'app' : 'grey-6'" />
          </q-item-section>
          <q-item-section>
            <q-item-label>
              {{ appPushStore.isEnabled
                  ? tt('appPush.on', 'Notifications activées')
                  : tt('appPush.off', 'Activer les notifications') }}
            </q-item-label>
            <q-item-label v-if="appPushStore.isEnabled" caption class="text-app cursor-pointer"
                          @click.stop="testPush">
              {{ tt('appPush.test', 'Envoyer un test') }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle :model-value="appPushStore.isEnabled" :disable="appPushStore.busy" color="app"
                      @update:model-value="togglePush" />
          </q-item-section>
        </q-item>
        <q-item v-else-if="appPushStore.isBlocked" class="text-grey-7">
          <q-item-section avatar><q-icon name="notifications_off" color="grey-6" /></q-item-section>
          <q-item-section>
            <q-item-label caption>{{ tt('appPush.blocked', 'Notifications bloquées par le navigateur.') }}</q-item-label>
          </q-item-section>
        </q-item>
        <!-- Son des notifications (toujours disponible, indépendant du push OS) -->
        <q-item>
          <q-item-section avatar>
            <q-icon :name="soundEnabled ? 'volume_up' : 'volume_off'"
                    :color="soundEnabled ? 'app' : 'grey-6'" />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ tt('appInbox.sound', 'Son des notifications') }}</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle :model-value="soundEnabled" color="app" @update:model-value="toggleSound" />
          </q-item-section>
        </q-item>

        <q-separator />

        <template v-if="appInboxStore.records.length">
          <q-item v-for="n in appInboxStore.records" :key="n.id"
                  clickable v-close-popup @click="open(n)"
                  class="notif-item" :class="{'notif-item--unread': !n.read}">
            <q-item-section avatar top>
              <!-- Message d'un utilisateur : sa photo ; sinon icône lu / non lu -->
              <q-avatar v-if="n.sender" size="32px">
                <img v-if="n.sender.image" :src="n.sender.image" alt="">
                <q-icon v-else name="person" />
                <q-badge v-if="!n.read" floating rounded color="app" class="notif-dot" />
              </q-avatar>
              <q-icon v-else :name="n.read ? 'notifications_none' : 'notifications_active'"
                      :color="n.read ? 'grey' : 'app'" />
            </q-item-section>
            <q-item-section>
              <q-item-label :class="{'text-weight-bold': !n.read}">{{ displayTitle(n) }}</q-item-label>
              <q-item-label v-if="displayBody(n)" caption lines="2">{{ displayBody(n) }}</q-item-label>
              <q-item-label caption class="text-grey-6">{{ timeAgo(n.dateCreated) }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-btn flat dense round size="sm" icon="close" color="grey-6" @click.stop="remove(n)">
                <q-tooltip>{{ tt('common.delete', 'Supprimer') }}</q-tooltip>
              </q-btn>
            </q-item-section>
          </q-item>
        </template>
        <q-item v-else>
          <q-item-section class="text-grey-6 text-center q-py-md">
            {{ tt('appInbox.empty', 'Aucune notification') }}
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>
  </q-btn>
</template>

<script>
import {mapStores} from 'pinia';
import {useAppInboxStore} from '@core/stores/app/appInboxStore';
import {useAppPushStore} from '@core/stores/app/appPushStore';
import {useAuthStore} from '@core/stores/authStore';
import {useLayoutStore} from '@core/stores/layoutStore';
import AppInboxService from '@core/services/app/appInboxService';
import AppPushService from '@core/services/app/appPushService';
import AppNotificationService from '@core/services/utils/appNotificationService';

export default {
  name: 'AppNotificationBell',

  data() {
    return {
      soundEnabled: AppNotificationService.isSoundEnabled(),
    };
  },

  computed: {
    ...mapStores(useAppInboxStore, useAppPushStore, useAuthStore, useLayoutStore),
    isMobile() {
      return this.layoutStore.isMobile;
    },
    // Sur mobile : menu quasi pleine largeur (et plus haut). Sur desktop : compact.
    menuStyle() {
      return this.isMobile
        ? 'width: 96vw; max-width: 96vw'
        : 'min-width: 320px; max-width: 380px';
    },
  },

  methods: {
    tt(key, fallback) {
      const v = this.$t(key);
      return (!v || v === key) ? fallback : v;
    },

    async togglePush(val) {
      if (val) await AppPushService.enable();
      else await AppPushService.disable();
    },

    async testPush() {
      await AppPushService.sendTest();
    },

    toggleSound(val) {
      this.soundEnabled = val;
      AppNotificationService.setSoundEnabled(val);
    },

    async open(n) {
      await AppInboxService.markRead(n.id);
      this.navigate(n);
    },

    navigate(n) {
      if (n.routeName && this.$router.hasRoute(n.routeName)) {
        this.$router.push({name: n.routeName, params: n.routeParams || {}}).catch(() => {});
      } else if (n.url && n.url !== '/') {
        this.$router.push(n.url).catch(() => {});
      }
    },

    async markAll() {
      await AppInboxService.markAllRead();
    },

    async remove(n) {
      await AppInboxService.remove(n.id);
    },

    /**
     * Titre/corps re-traduits dynamiquement selon la langue SÉLECTIONNÉE, via les
     * clés notif.<type>.title|body (c_app.app_translation) + params. Repli sur le
     * texte stocké (rendu côté backend dans la langue du destinataire) si la clé
     * n'existe pas dans la locale courante.
     */
    displayTitle(n) { return this.trNotif(n, 'title'); },
    displayBody(n) { return this.trNotif(n, 'body'); },
    trNotif(n, field) {
      if (n && n.type) {
        const key = 'notif.' + n.type + '.' + field;
        if (this.$te && this.$te(key)) return this.$t(key, n.params || {});
      }
      return (n && n[field]) || '';
    },

    timeAgo(date) {
      if (!date) return '';
      const d = new Date(date);
      const s = Math.floor((Date.now() - d.getTime()) / 1000);
      if (s < 60) return "à l'instant";
      if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
      if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
      if (s < 604800) return `il y a ${Math.floor(s / 86400)} j`;
      return d.toLocaleDateString();
    },
  },

  async mounted() {
    if (!this.authStore?.isLoggedIn) return;
    await AppInboxService.load();

    // Deep-link depuis un clic sur notification push (url /?notif=ID).
    const notifId = new URLSearchParams(window.location.search).get('notif');
    if (notifId) {
      const n = this.appInboxStore.records.find(r => String(r.id) === String(notifId));
      if (n) this.open(n);
      const url = new URL(window.location.href);
      url.searchParams.delete('notif');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  },
}
</script>

<!-- Le menu est téléporté (hors scope) : on arrondit/clippe via une classe globale dédiée. -->
<style lang="scss">
.q-menu.app-notif-menu {
  border-radius: 14px;
  overflow: hidden;        /* le dégradé de l'en-tête épouse les coins arrondis */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}
.q-menu.app-notif-menu .q-list { padding: 0; }
</style>

<style scoped lang="scss">
.notif-panel {
  padding: 0;
}

/* En-tête moderne : dégradé à la couleur d'app + formes géométriques (style SOS). */
.notif-head {
  position: relative;
  overflow: hidden;
  padding: 14px 12px 12px 16px;
  color: #fff;
  background: linear-gradient(135deg,
    var(--q-color-app, #4F46E5) 0%,
    color-mix(in srgb, var(--q-color-app, #4F46E5) 72%, #000) 100%);

  &__row {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
  }
  &__title {
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
}
.nh-shape { position: absolute; pointer-events: none; display: block; }
.nh-circle {
  width: 130px; height: 130px;
  top: -68px; right: -34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
}
.nh-ring {
  width: 96px; height: 96px;
  bottom: -60px; right: 28%;
  border-radius: 50%;
  border: 15px solid rgba(255, 255, 255, 0.10);
}
.nh-diamond {
  width: 46px; height: 46px;
  top: 6px; left: 40%;
  background: rgba(0, 0, 0, 0.07);
  transform: rotate(45deg);
}

/* Items : non-lu surligné, bouton supprimer discret. */
.notif-item--unread {
  background: rgba(var(--q-color-app-rgb, 79, 70, 229), 0.07);
}
.notif-item .q-btn {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}
.notif-item:hover .q-btn {
  opacity: 1;
}
</style>
